import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Bookmark, ArrowLeft, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLists } from "@/contexts/ListsContext";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import MovieCard from "@/components/MovieCard";
import DetailModal from "@/components/DetailModal";
import { toast } from "sonner";

export default function ListPage({ type = "watchlist" }) {
  const { user, loading } = useAuth();
  const { refresh } = useLists();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(true);
  const [active, setActive] = useState(null);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/me/${type}`);
      setItems(data);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (loading) return;
    if (!user) return;
    load();
    // eslint-disable-next-line
  }, [user, loading, type]);

  if (loading) return null;
  if (!user) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center flex-col gap-4 text-center px-6">
        <h1 className="font-display text-3xl font-bold">Sign in to view your {type}</h1>
        <Link to="/login" className="bg-[var(--tt-primary)] hover:bg-[var(--tt-primary-strong)] text-white font-semibold px-6 py-2.5 rounded-full">Login</Link>
      </div>
    );
  }

  const Icon = type === "favorites" ? Heart : Bookmark;
  const color = type === "favorites" ? "#FF2A6D" : "#F9A826";

  const remove = async (it) => {
    try {
      await api.delete(`/me/${type}/${it.media_type}/${it.tmdb_id}`);
      toast.success("Removed");
      load(); refresh();
    } catch { toast.error("Failed to remove"); }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-black text-white">
      <Sidebar />
      <div className="flex-1 h-full overflow-y-auto flex flex-col">
        <Header />
        <div className="px-4 md:px-8 py-6 flex-1">
          <div className="flex items-center gap-3 mb-6">
            <Link to="/" className="text-[var(--tt-text-muted)] hover:text-white" data-testid={`${type}-back-home`}>
              <ArrowLeft className="w-4 h-4" />
            </Link>
            <h1 className="font-display text-3xl font-bold flex items-center gap-3">
              <Icon className="w-7 h-7" style={{ color }} />
              My {type === "favorites" ? "Favorites" : "Watchlist"}
            </h1>
            <span className="font-mono-tt text-xs bg-[var(--tt-surface-2)] border border-[var(--tt-border)] px-2 py-1 rounded text-[var(--tt-text-muted)]">
              {items.length}
            </span>
          </div>

          {busy ? (
            <div className="text-[var(--tt-text-muted)]">Loading...</div>
          ) : items.length === 0 ? (
            <div className="text-center py-20">
              <Icon className="w-16 h-16 mx-auto mb-4 opacity-30" style={{ color }} />
              <div className="text-lg font-display font-bold">Nothing here yet</div>
              <div className="text-sm text-[var(--tt-text-muted)] mt-2">
                Tap the {type === "favorites" ? "heart" : "bookmark"} on any movie or TV show to add it here.
              </div>
              <Link to="/" className="inline-block mt-6 bg-[var(--tt-primary)] hover:bg-[var(--tt-primary-strong)] text-white font-semibold px-5 py-2 rounded-full">
                Browse
              </Link>
            </div>
          ) : (
            <div
              data-testid={`${type}-grid`}
              className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6"
            >
              {items.map((it) => (
                <div key={`${it.media_type}-${it.tmdb_id}`} className="relative group">
                  <MovieCard
                    item={{
                      id: it.tmdb_id,
                      media_type: it.media_type,
                      title: it.title,
                      poster: it.poster,
                      backdrop: it.backdrop,
                      year: it.year,
                      rating: it.rating,
                      overview: it.overview,
                    }}
                    onOpen={setActive}
                  />
                  <button
                    onClick={() => remove(it)}
                    data-testid={`${type}-remove-${it.tmdb_id}`}
                    className="absolute top-2 right-2 p-2 rounded-full bg-red-500/80 hover:bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Remove"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        <Footer />
      </div>
      <DetailModal item={active} onClose={() => setActive(null)} />
    </div>
  );
}
